/**
 * PATH: packages/ui/src/kanban/components/board-view/cardFaceRecipe.ts
 * FILE_ID: UI-KANBAN-CARD-FACE-RECIPE-001
 * PURPOSE: Card-face field / rail / strip / menu / extra catalogs and resolve.
 * ROLE IN ARCHITECTURE: Persist contract sibling of cardFacePrefs blocks.
 * MAIN EXPORTS: field/place catalogs, coerceRecipeChrome, resolveRecipeChrome,
 *   sparseRecipeChrome, menuShows (alsoShow + reply need their allocator)
 * NON-RESPONSIBILITIES: Does not persist or render.
 * NOTES FOR FUTURE AI: Unset means current live chrome stays on. Inbox due
 *   and allocator defaults stay in resolveCardFacePrefs. Hide omits chrome.
 */

export const CARD_FACE_FIELD_IDS = [
  'due',
  'pin',
  'aiConfidence',
  'priority',
  'colorCover',
  'sensitive',
  'personalCopy',
  'suggestedLane',
  'sender',
  'received',
  'mailbox',
  'mail',
  'replies',
  'unseen',
  'previousDue',
  'actionNote',
  'actionDate',
  'previousAction',
  'blocked',
  'snooze',
  'assignees',
  'jobRef',
  'description',
  'attachments',
  'checklist',
  'comments',
  'presence',
  'related',
  'subcards',
  'merged',
  'rollup',
  'workbook',
] as const

export type CardFaceFieldId = (typeof CARD_FACE_FIELD_IDS)[number]

export const CARD_FACE_RAIL_IDS = [
  'priority',
  'due',
  'assign',
  'move',
  'archive',
  'entries',
  'allocator',
  'more',
] as const

export type CardFaceRailId = (typeof CARD_FACE_RAIL_IDS)[number]
export type CardFacePlaceMode = 'rail' | 'menu' | 'off'

export const CARD_FACE_STRIP_IDS = ['color', 'workbook', 'flowchart'] as const
export type CardFaceStripId = (typeof CARD_FACE_STRIP_IDS)[number]
export type CardFaceOnOff = 'on' | 'off'
export type CardFaceMenuPlace = 'menu' | 'off'

export const CARD_FACE_MENU_IDS = [
  'notebook',
  'templates',
  'actionDate',
  'followup',
  'snoozeMenu',
  'blockMenu',
  'reply',
  'meeting',
  'subcard',
  'promote',
  'copyBoard',
  'duplicate',
  'copyLink',
  'pinToggle',
  'checklistPanel',
  'delete',
  'alsoShow',
  'openWorkbook',
] as const

export type CardFaceMenuId = (typeof CARD_FACE_MENU_IDS)[number]

export const CARD_FACE_EXTRA_IDS = [
  'thread',
  'quickCapture',
  'checklistExpand',
] as const
export type CardFaceExtraId = (typeof CARD_FACE_EXTRA_IDS)[number]

export type CardFaceRailMode = 'hover' | 'always' | 'hidden'
/** rest = short tile until hover. open = the tile stays tall with no hover. */
export type CardFaceTileBody = 'rest' | 'open'
export type CardFaceTileOpen = 'host' | 'expand'
export type CardFaceCompleteMode = 'hover' | 'always' | 'off'
export type CardFaceComposeSeat = 'rail' | 'strip' | 'menu' | 'off'

export type CardFaceFieldMap = Partial<Record<CardFaceFieldId, boolean>>
export type CardFacePlaceMap = Partial<Record<CardFaceRailId, CardFacePlaceMode>>
export type CardFaceStripMap = Partial<Record<CardFaceStripId, CardFaceOnOff>>
export type CardFaceMenuMap = Partial<Record<CardFaceMenuId, CardFaceMenuPlace>>
export type CardFaceExtraMap = Partial<Record<CardFaceExtraId, CardFaceOnOff>>

export interface CardFaceRecipeChrome {
  fields: CardFaceFieldMap
  place: CardFacePlaceMap
  strip: CardFaceStripMap
  menu: CardFaceMenuMap
  extras: CardFaceExtraMap
  compose?: CardFaceComposeSeat
  railMode?: CardFaceRailMode
  tileBody?: CardFaceTileBody
  tileOpen?: CardFaceTileOpen
  complete?: CardFaceCompleteMode
}

export interface ResolvedCardFaceRecipe extends CardFaceRecipeChrome {
  fields: Record<CardFaceFieldId, boolean>
  place: Record<CardFaceRailId, CardFacePlaceMode>
  strip: Record<CardFaceStripId, CardFaceOnOff>
  menu: Record<CardFaceMenuId, CardFaceMenuPlace>
  extras: Record<CardFaceExtraId, CardFaceOnOff>
  compose: CardFaceComposeSeat
  railMode: CardFaceRailMode
  tileBody: CardFaceTileBody
  tileOpen: CardFaceTileOpen
  complete: CardFaceCompleteMode
}

export const FIELD_LABELS: Record<CardFaceFieldId, string> = {
  due: 'Due date',
  pin: 'Pin',
  aiConfidence: 'AI %',
  priority: 'Priority edge',
  colorCover: 'Colour cover',
  sensitive: 'Sensitive',
  personalCopy: 'Personal copy',
  suggestedLane: 'AI suggested lane',
  sender: 'Sender',
  received: 'Received',
  mailbox: 'Mailbox',
  mail: 'Mail open',
  replies: 'Replies',
  unseen: 'Unseen',
  previousDue: 'Old due date',
  actionNote: 'Next action',
  actionDate: 'Action date',
  previousAction: 'Old action date',
  blocked: 'Blocked',
  snooze: 'Snooze',
  assignees: 'People',
  jobRef: 'Job',
  description: 'Description',
  attachments: 'Files',
  checklist: 'Checklist count',
  comments: 'Comments',
  presence: 'Shown on other boards',
  related: 'Related',
  subcards: 'Sub-cards',
  merged: 'Merged',
  rollup: 'Money rollup',
  workbook: 'Sheet chip',
}

export const RAIL_LABELS: Record<CardFaceRailId, string> = {
  priority: 'Priority',
  due: 'Due date',
  assign: 'Assign',
  move: 'Move',
  archive: 'Archive',
  entries: 'Open entries',
  allocator: 'Also show',
  more: 'More menu',
}

export const STRIP_LABELS: Record<CardFaceStripId, string> = {
  color: 'Colour and labels',
  workbook: 'Workbook',
  flowchart: 'Flowchart',
}

export const MENU_LABELS: Record<CardFaceMenuId, string> = {
  notebook: 'Convert to notebook',
  templates: 'Action templates',
  actionDate: 'Set action date',
  followup: 'Set follow-up date',
  snoozeMenu: 'Snooze',
  blockMenu: 'Block',
  reply: 'Reply / forward',
  meeting: 'Schedule meeting',
  subcard: 'Make sub-card',
  promote: 'Promote to top level',
  copyBoard: 'Copy to board',
  duplicate: 'Duplicate',
  copyLink: 'Copy card link',
  pinToggle: 'Pin / unpin',
  checklistPanel: 'Show / hide checklist',
  delete: 'Delete',
  alsoShow: 'Also show in lane',
  openWorkbook: 'Open workbook',
}

export const EXTRA_LABELS: Record<CardFaceExtraId, string> = {
  thread: 'Thread peek',
  quickCapture: 'Quick-capture row',
  checklistExpand: 'Expand checklist panel',
}

function isPlace(value: unknown): value is CardFacePlaceMode {
  return value === 'rail' || value === 'menu' || value === 'off'
}

function isOnOff(value: unknown): value is CardFaceOnOff {
  return value === 'on' || value === 'off'
}

function isMenuPlace(value: unknown): value is CardFaceMenuPlace {
  return value === 'menu' || value === 'off'
}

export function coerceFields(raw: unknown): CardFaceFieldMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CardFaceFieldMap = {}
  for (const id of CARD_FACE_FIELD_IDS) {
    const value = (raw as Record<string, unknown>)[id]
    if (typeof value === 'boolean') next[id] = value
  }
  return next
}

export function coercePlace(raw: unknown): CardFacePlaceMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CardFacePlaceMap = {}
  for (const id of CARD_FACE_RAIL_IDS) {
    const value = (raw as Record<string, unknown>)[id]
    if (isPlace(value)) next[id] = value
  }
  return next
}

export function coerceStrip(raw: unknown): CardFaceStripMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CardFaceStripMap = {}
  for (const id of CARD_FACE_STRIP_IDS) {
    const value = (raw as Record<string, unknown>)[id]
    if (isOnOff(value)) next[id] = value
  }
  return next
}

export function coerceMenu(raw: unknown): CardFaceMenuMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CardFaceMenuMap = {}
  for (const id of CARD_FACE_MENU_IDS) {
    const value = (raw as Record<string, unknown>)[id]
    if (isMenuPlace(value)) next[id] = value
  }
  return next
}

export function coerceExtras(raw: unknown): CardFaceExtraMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CardFaceExtraMap = {}
  for (const id of CARD_FACE_EXTRA_IDS) {
    const value = (raw as Record<string, unknown>)[id]
    if (isOnOff(value)) next[id] = value
  }
  return next
}

export function coerceRailMode(raw: unknown): CardFaceRailMode | undefined {
  return raw === 'hover' || raw === 'always' || raw === 'hidden' ? raw : undefined
}

export function coerceTileBody(raw: unknown): CardFaceTileBody | undefined {
  return raw === 'rest' || raw === 'open' ? raw : undefined
}

export function coerceTileOpen(raw: unknown): CardFaceTileOpen | undefined {
  return raw === 'host' || raw === 'expand' ? raw : undefined
}

export function coerceComplete(raw: unknown): CardFaceCompleteMode | undefined {
  return raw === 'hover' || raw === 'always' || raw === 'off' ? raw : undefined
}

export function coerceCompose(raw: unknown): CardFaceComposeSeat | undefined {
  return raw === 'rail' || raw === 'strip' || raw === 'menu' || raw === 'off'
    ? raw
    : undefined
}

export function coerceRecipeChrome(raw: Record<string, unknown>): CardFaceRecipeChrome {
  return {
    fields: coerceFields(raw.fields),
    place: coercePlace(raw.place),
    strip: coerceStrip(raw.strip),
    menu: coerceMenu(raw.menu),
    extras: coerceExtras(raw.extras),
    compose: coerceCompose(raw.compose),
    railMode: coerceRailMode(raw.railMode),
    tileBody: coerceTileBody(raw.tileBody),
    tileOpen: coerceTileOpen(raw.tileOpen),
    complete: coerceComplete(raw.complete),
  }
}

export function fieldDefault(id: CardFaceFieldId, isInbox: boolean): boolean {
  if (id === 'due') return !isInbox
  return true
}

export function placeDefault(id: CardFaceRailId, isInbox: boolean): CardFacePlaceMode {
  if (id === 'allocator') return isInbox ? 'rail' : 'menu'
  if (id === 'due') return 'menu'
  if (id === 'entries') return 'off'
  return 'rail'
}

export function resolveRecipeChrome(
  chrome: CardFaceRecipeChrome,
  override: Partial<CardFaceRecipeChrome> | undefined,
  isInbox: boolean,
): ResolvedCardFaceRecipe {
  const fields = {} as Record<CardFaceFieldId, boolean>
  for (const id of CARD_FACE_FIELD_IDS) {
    fields[id] =
      override?.fields?.[id] ?? chrome.fields[id] ?? fieldDefault(id, isInbox)
  }
  const place = {} as Record<CardFaceRailId, CardFacePlaceMode>
  for (const id of CARD_FACE_RAIL_IDS) {
    place[id] =
      override?.place?.[id] ?? chrome.place[id] ?? placeDefault(id, isInbox)
  }
  const strip = {} as Record<CardFaceStripId, CardFaceOnOff>
  for (const id of CARD_FACE_STRIP_IDS) {
    strip[id] = override?.strip?.[id] ?? chrome.strip[id] ?? 'on'
  }
  const menu = {} as Record<CardFaceMenuId, CardFaceMenuPlace>
  for (const id of CARD_FACE_MENU_IDS) {
    menu[id] = override?.menu?.[id] ?? chrome.menu[id] ?? 'menu'
  }
  const extras = {} as Record<CardFaceExtraId, CardFaceOnOff>
  for (const id of CARD_FACE_EXTRA_IDS) {
    extras[id] = override?.extras?.[id] ?? chrome.extras[id] ?? 'on'
  }
  return {
    fields,
    place,
    strip,
    menu,
    extras,
    compose: override?.compose ?? chrome.compose ?? 'strip',
    railMode: override?.railMode ?? chrome.railMode ?? 'hover',
    tileBody: override?.tileBody ?? chrome.tileBody ?? 'rest',
    tileOpen: override?.tileOpen ?? chrome.tileOpen ?? 'host',
    complete: override?.complete ?? chrome.complete ?? 'hover',
  }
}

export function railShows(
  place: Partial<Record<CardFaceRailId, CardFacePlaceMode>>,
  id: CardFaceRailId,
): boolean {
  return place[id] === 'rail'
}

export function menuShows(
  recipe: {
    menu: Partial<Record<CardFaceMenuId, CardFaceMenuPlace>>
    place: Partial<Record<CardFaceRailId, CardFacePlaceMode>>
    compose?: CardFaceComposeSeat
  },
  id: CardFaceMenuId,
): boolean {
  if (id === 'alsoShow') {
    return recipe.place.allocator === 'menu' && recipe.menu.alsoShow === 'menu'
  }
  if (id === 'reply') {
    return recipe.compose === 'menu' && recipe.menu.reply === 'menu'
  }
  return recipe.menu[id] === 'menu'
}

function keepMapEntry<K extends string, V>(
  ids: readonly K[],
  merged: Partial<Record<K, V>>,
  keep: (id: K, value: V) => boolean,
): Partial<Record<K, V>> {
  const next: Partial<Record<K, V>> = {}
  for (const id of ids) {
    const value = merged[id]
    if (value !== undefined && keep(id, value)) next[id] = value
  }
  return next
}

/** Drop board-chrome keys that already match the stored global recipe. */
export function sparseRecipeChrome(
  global: CardFaceRecipeChrome,
  merged: CardFaceRecipeChrome,
): CardFaceRecipeChrome {
  return {
    fields: keepMapEntry(CARD_FACE_FIELD_IDS, merged.fields, (id, value) => {
      if (global.fields[id] !== undefined) return value !== global.fields[id]
      const job = fieldDefault(id, false)
      return job !== fieldDefault(id, true) || value !== job
    }),
    place: keepMapEntry(CARD_FACE_RAIL_IDS, merged.place, (id, value) => {
      if (global.place[id] !== undefined) return value !== global.place[id]
      const job = placeDefault(id, false)
      return job !== placeDefault(id, true) || value !== job
    }),
    strip: keepMapEntry(
      CARD_FACE_STRIP_IDS,
      merged.strip,
      (id, value) => value !== (global.strip[id] ?? 'on'),
    ),
    menu: keepMapEntry(
      CARD_FACE_MENU_IDS,
      merged.menu,
      (id, value) => value !== (global.menu[id] ?? 'menu'),
    ),
    extras: keepMapEntry(
      CARD_FACE_EXTRA_IDS,
      merged.extras,
      (id, value) => value !== (global.extras[id] ?? 'on'),
    ),
    compose:
      merged.compose !== undefined && merged.compose !== (global.compose ?? 'strip')
        ? merged.compose
        : undefined,
    railMode:
      merged.railMode !== undefined && merged.railMode !== (global.railMode ?? 'hover')
        ? merged.railMode
        : undefined,
    tileBody:
      merged.tileBody !== undefined && merged.tileBody !== global.tileBody
        ? merged.tileBody
        : undefined,
    tileOpen:
      merged.tileOpen !== undefined && merged.tileOpen !== (global.tileOpen ?? 'host')
        ? merged.tileOpen
        : undefined,
    complete:
      merged.complete !== undefined && merged.complete !== (global.complete ?? 'hover')
        ? merged.complete
        : undefined,
  }
}

export function recipeChromeIsEmpty(chrome: CardFaceRecipeChrome): boolean {
  return (
    Object.keys(chrome.fields).length === 0 &&
    Object.keys(chrome.place).length === 0 &&
    Object.keys(chrome.strip).length === 0 &&
    Object.keys(chrome.menu).length === 0 &&
    Object.keys(chrome.extras).length === 0 &&
    chrome.compose === undefined &&
    chrome.railMode === undefined &&
    chrome.tileBody === undefined &&
    chrome.tileOpen === undefined &&
    chrome.complete === undefined
  )
}
